import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Store, ExternalLink, Loader, Package, ShoppingCart, Plus, Minus, Trash2, CreditCard, X, AlertCircle, CheckCircle, Ticket, Tag, ChevronLeft, ChevronRight, User, MapPin, Phone, FileText, ArrowLeft, ShieldCheck, Truck, Headset, Sparkles, Instagram, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de Card de Produto — versão premium (aspect-square, badge de desconto, hover elevado)
const ProductCard = ({ product, images, inCart, paymentsEnabled, onAddToCart, theme }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  const cardRadius =
    theme?.card_style === 'square' ? 'rounded-none' :
    theme?.card_style === 'minimal' ? 'rounded-md' :
    'rounded-2xl';

  const hasDiscount = product.original_price > product.discounted_price && product.original_price > 0;
  const discountPercent = hasDiscount
    ? Math.round(((product.original_price - product.discounted_price) / product.original_price) * 100)
    : 0;

  return (
    <div
      className={`${cardRadius} group relative overflow-hidden transition-all duration-300 hover:-translate-y-1`}
      style={{
        backgroundColor: theme?.card_color || '#ffffff',
        boxShadow: isHovering
          ? '0 20px 40px -12px rgba(0,0,0,0.18)'
          : '0 2px 8px -2px rgba(0,0,0,0.06)',
        border: `1px solid ${theme?.card_color && theme.card_color !== '#ffffff' ? 'transparent' : '#f0f0f5'}`
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-testid={`store-product-${product.product_id}`}
    >
      {/* Media — proporção quadrada premium */}
      {images.length > 0 && (
        <div className="relative aspect-square w-full overflow-hidden bg-gray-50">
          <img
            src={images[currentImageIndex]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />

          {/* Badge de desconto */}
          {hasDiscount && (
            <div
              className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold shadow-sm"
              style={{
                backgroundColor: theme?.primary_color || '#4f46e5',
                color: theme?.button_text_color || '#ffffff'
              }}
            >
              -{discountPercent}%
            </div>
          )}

          {/* Selo "Novo" (quando não tem desconto) */}
          {!hasDiscount && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-white/90 backdrop-blur-sm text-gray-800 shadow-sm">
              Novo
            </div>
          )}

          {/* Navegação do carrossel */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-md"
                aria-label="Imagem anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-md"
                aria-label="Próxima imagem"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/60 w-1.5'
                    }`}
                    aria-label={`Ir para imagem ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Conteúdo do card */}
      <div className="p-5">
        <h3
          className="text-base font-semibold mb-1.5 line-clamp-2 leading-snug min-h-[2.6rem]"
          style={{ color: theme?.card_text_color || '#111827' }}
        >
          {product.name}
        </h3>
        <p
          className="text-xs mb-4 line-clamp-2 leading-relaxed"
          style={{ color: theme?.card_text_color ? theme.card_text_color + '99' : '#6b7280' }}
        >
          {product.description}
        </p>

        {/* Preço */}
        <div className="mb-4">
          {hasDiscount && (
            <div className="text-xs line-through mb-0.5" style={{ color: theme?.card_text_color ? theme.card_text_color + '77' : '#9ca3af' }}>
              De R$ {product.original_price.toFixed(2)}
            </div>
          )}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl font-bold tracking-tight" style={{ color: theme?.primary_color || '#4f46e5' }}>
              R$ {product.discounted_price.toFixed(2)}
            </span>
            {hasDiscount && (
              <span className="text-xs font-semibold" style={{ color: '#059669' }}>
                Economize R$ {(product.original_price - product.discounted_price).toFixed(2)}
              </span>
            )}
          </div>
          <div className="text-xs mt-1" style={{ color: theme?.card_text_color ? theme.card_text_color + '99' : '#6b7280' }}>
            Em até 12x sem juros
          </div>
        </div>

        {/* CTA */}
        {paymentsEnabled ? (
          <Button
            onClick={onAddToCart}
            className="w-full font-semibold transition-all hover:opacity-90 rounded-lg"
            style={{
              backgroundColor: inCart ? '#059669' : (theme?.button_color || '#4f46e5'),
              color: theme?.button_text_color || '#ffffff'
            }}
            data-testid={`add-to-cart-${product.product_id}`}
          >
            {inCart ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                No Carrinho ({inCart.quantity})
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4 mr-2" />
                Adicionar ao Carrinho
              </>
            )}
          </Button>
        ) : product.product_link ? (
          <a
            href={product.product_link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center space-x-2 font-semibold px-6 py-2.5 rounded-lg transition-all hover:opacity-90"
            style={{
              backgroundColor: theme?.button_color || '#4f46e5',
              color: theme?.button_text_color || '#ffffff'
            }}
          >
            <span>Ver Produto</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : null}
      </div>
    </div>
  );
};

export const StorePage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [theme, setTheme] = useState(null);
  
  // Carrinho
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  
  // Cupom
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Categorias
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allProducts, setAllProducts] = useState([]);

  // Checkout Info (Cliente)
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutInfo, setCheckoutInfo] = useState({
    full_name: '', cpf: '', email: '', phone: '',
    address: { cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' },
    order_notes: ''
  });
  const [savingCheckoutInfo, setSavingCheckoutInfo] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    fetchStore();
  }, [storeId]);

  useEffect(() => {
    // Filtrar produtos quando a categoria muda
    if (selectedCategory === 'all') {
      setProducts(allProducts);
    } else {
      fetchStoreWithCategory(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchStore = async () => {
    try {
      const [storeRes, mpRes, themeRes] = await Promise.all([
        axios.get(`${API_URL}/store/${storeId}`),
        axios.get(`${API_URL}/store/${storeId}/mp-key`).catch(() => null),
        axios.get(`${API_URL}/store/${storeId}/theme`).catch(() => null)
      ]);
      
      setStore(storeRes.data.store);
      setProducts(storeRes.data.products);
      setAllProducts(storeRes.data.products);
      setCategories(storeRes.data.categories || []);
      
      if (mpRes?.data?.payments_enabled) {
        setPaymentsEnabled(true);
      }

      if (themeRes?.data) {
        setTheme(themeRes.data);
      }
    } catch (error) {
      toast.error('Loja não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreWithCategory = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/store/${storeId}?category=${categoryId}`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Erro ao filtrar por categoria:', error);
    }
  };

  // Funções do carrinho
  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.product_id);
    
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.product_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.product_id,
        name: product.name,
        unit_price: product.discounted_price,
        image_url: product.image_url,
        quantity: 1
      }]);
    }
    
    toast.success('Produto adicionado ao carrinho!');
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getFinalTotal = () => {
    const subtotal = getCartTotal();
    const discount = appliedCoupon?.discount_amount || 0;
    return Math.max(0, subtotal - discount);
  };

  // Validar cupom
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Digite o código do cupom');
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await axios.post(
        `${API_URL}/store/${storeId}/validate-coupon`,
        { code: couponCode, subtotal: getCartTotal() }
      );

      if (response.data.valid) {
        setAppliedCoupon({
          code: response.data.coupon_code,
          discount_amount: response.data.discount_amount
        });
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error('Erro ao validar cupom');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  // Checkout
  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para continuar com a compra');
      navigate('/login', { state: { from: `/loja/${storeId}` } });
      return;
    }

    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    // Show checkout form first
    try {
      const profileRes = await axios.get(`${API_URL}/customer/profile`, { withCredentials: true });
      const p = profileRes.data;
      setCheckoutInfo(prev => ({
        full_name: p.full_name || user?.name || '',
        cpf: p.cpf || '',
        email: p.email || user?.email || '',
        phone: p.phone || '',
        address: p.address || prev.address,
        order_notes: ''
      }));
    } catch (e) {
      setCheckoutInfo(prev => ({
        ...prev,
        full_name: user?.name || '',
        email: user?.email || '',
      }));
    }
    setShowCheckoutForm(true);
  };

  const formatCPFInput = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  const formatPhoneInput = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  };

  const handleCheckoutCepLookup = async (cep) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await axios.get(`${API_URL}/cep/${digits}`);
      if (res.data.success) {
        const a = res.data.address;
        setCheckoutInfo(p => ({
          ...p,
          address: {
            ...p.address,
            cep: a.cep || digits,
            street: a.street || p.address.street,
            neighborhood: a.neighborhood || p.address.neighborhood,
            city: a.city || p.address.city,
            state: a.state || p.address.state,
          }
        }));
        toast.success('Endereço preenchido!');
      }
    } catch (e) {
      toast.error('CEP não encontrado');
    } finally { setCepLoading(false); }
  };

  const processCheckout = async () => {
    if (!checkoutInfo.full_name.trim()) {
      toast.error('Preencha seu nome completo');
      return;
    }

    setSavingCheckoutInfo(true);

    try {
      // Save customer info
      await axios.put(
        `${API_URL}/customer/checkout-info/${storeId}`,
        checkoutInfo,
        { withCredentials: true }
      );

      // Get seller_id
      const seller = await axios.get(`${API_URL}/store/${storeId}`);
      const sellerId = seller.data.products[0]?.seller_id;
      if (!sellerId) {
        toast.error('Erro ao identificar vendedor');
        return;
      }

      // Create order
      const orderRes = await axios.post(
        `${API_URL}/orders`,
        { seller_id: sellerId, items: cart },
        { withCredentials: true }
      );

      if (!orderRes.data.success) throw new Error('Erro ao criar pedido');
      const orderId = orderRes.data.order_id;

      // Novo fluxo: Checkout Transparente unificado (dentro do Needi)
      navigate(`/checkout?target=order&target_id=${orderId}`);
    } catch (error) {
      console.error('Erro no checkout:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar checkout');
    } finally {
      setSavingCheckoutInfo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando loja...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-center max-w-md">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loja não encontrada</h2>
          <p className="text-gray-600">Verifique o ID e tente novamente</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme?.background_color || '#fafafa' }}>
      {/* HEADER STICKY — nome + carrinho */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-md"
        style={{
          backgroundColor: (theme?.background_color || '#ffffff') + 'ee',
          borderColor: '#e5e7eb'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {theme?.profile_image ? (
              <img src={theme.profile_image} alt="Logo" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${theme?.primary_color || '#4f46e5'}, ${theme?.secondary_color || '#7c3aed'})` }}
              >
                <Store className="w-4 h-4 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-bold text-sm sm:text-base truncate" style={{ color: theme?.text_color || '#111827' }}>
                {theme?.store_name || store.name}
              </div>
              <div className="text-xs opacity-60 truncate" style={{ color: theme?.text_color || '#111827' }}>
                Loja oficial
              </div>
            </div>
          </div>

          {paymentsEnabled && cart.length > 0 && (
            <Button
              onClick={() => setShowCart(true)}
              className="relative shrink-0 rounded-full"
              size="sm"
              style={{
                backgroundColor: theme?.primary_color || '#4f46e5',
                color: theme?.button_text_color || '#ffffff'
              }}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Carrinho</span>
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                {getCartCount()}
              </span>
            </Button>
          )}
        </div>
      </header>

      {/* HERO — imersivo, com CTA e overlay elegante */}
      <section
        className="relative overflow-hidden"
        style={theme?.banner_image ? {
          backgroundImage: `url(${theme.banner_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {
          background: `linear-gradient(135deg, ${theme?.primary_color || '#4f46e5'} 0%, ${theme?.secondary_color || '#7c3aed'} 100%)`
        }}
      >
        {/* Overlay para dar profundidade e legibilidade */}
        <div
          className="absolute inset-0"
          style={{
            background: theme?.banner_image
              ? 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.35) 100%)'
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 md:py-28 z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1 text-white text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Loja oficial • Compras 100% seguras</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight leading-tight" data-testid="store-name">
              {theme?.store_name || store.name}
            </h1>

            <p className="text-white/85 text-base sm:text-lg mb-8 max-w-lg leading-relaxed">
              {store.seller_name ? `Selecionados com carinho por ${store.seller_name}. ` : ''}
              Aproveite condições exclusivas e frete rápido para todo o Brasil.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="#produtos"
                className="inline-flex items-center gap-2 bg-white text-gray-900 font-semibold px-6 py-3 rounded-full hover:scale-105 transition-transform shadow-lg"
                data-testid="hero-shop-cta"
              >
                <Package className="w-4 h-4" />
                Ver produtos
              </a>
              {paymentsEnabled && (
                <button
                  onClick={() => setShowCart(true)}
                  disabled={cart.length === 0}
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-medium px-6 py-3 rounded-full hover:bg-white/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Meu carrinho {cart.length > 0 ? `(${getCartCount()})` : ''}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BARRA DE BENEFÍCIOS */}
      <div className="border-b" style={{ backgroundColor: theme?.card_color || '#ffffff', borderColor: '#e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, title: 'Envio rápido', subtitle: 'Para todo o Brasil' },
            { icon: ShieldCheck, title: 'Compra segura', subtitle: 'Pagamento protegido' },
            { icon: CreditCard, title: 'Parcele em 12x', subtitle: 'Cartão de crédito' },
            { icon: Headset, title: 'Suporte real', subtitle: 'Fale com o vendedor' },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: (theme?.primary_color || '#4f46e5') + '15' }}
              >
                <b.icon className="w-5 h-5" style={{ color: theme?.primary_color || '#4f46e5' }} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: theme?.text_color || '#111827' }}>{b.title}</div>
                <div className="text-xs opacity-70 truncate" style={{ color: theme?.text_color || '#111827' }}>{b.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Aviso de pagamentos indisponíveis */}
      {!paymentsEnabled && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">
              Esta loja ainda não aceita pagamentos online. Entre em contato com o vendedor para combinar a forma de pagamento.
            </p>
          </div>
        </div>
      )}

      {/* PRODUTOS */}
      <section id="produtos" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        {/* Cabeçalho da seção */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase mb-2 opacity-60" style={{ color: theme?.primary_color || '#4f46e5' }}>
              Nossa coleção
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: theme?.text_color || '#111827' }}>
              {selectedCategory !== 'all' && categories.find(c => c.category_id === selectedCategory)?.name
                ? categories.find(c => c.category_id === selectedCategory).name
                : 'Produtos em destaque'}
            </h2>
            <p className="text-sm mt-1 opacity-70" style={{ color: theme?.text_color || '#111827' }}>
              {products.length} {products.length === 1 ? 'produto disponível' : 'produtos disponíveis'}
            </p>
          </div>
        </div>

        {/* Filtros de Categoria */}
        {categories.length > 1 && (
          <div className="mb-10 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex gap-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategory(cat.category_id)}
                  className="px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border"
                  style={
                    selectedCategory === cat.category_id
                      ? {
                          backgroundColor: theme?.primary_color || '#4f46e5',
                          color: theme?.button_text_color || '#ffffff',
                          borderColor: theme?.primary_color || '#4f46e5'
                        }
                      : {
                          backgroundColor: theme?.card_color || '#ffffff',
                          color: theme?.text_color || '#374151',
                          borderColor: '#e5e7eb'
                        }
                  }
                  data-testid={`category-filter-${cat.category_id}`}
                >
                  {cat.name}
                  {cat.product_count > 0 && (
                    <span className="ml-1.5 text-xs opacity-70">
                      ({cat.product_count})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <div className="text-center py-20 rounded-2xl" style={{ backgroundColor: theme?.card_color || '#ffffff', border: '1px dashed #e5e7eb' }}>
            <Package className="w-16 h-16 mx-auto mb-4" style={{ color: theme?.text_color ? theme.text_color + '66' : '#9ca3af' }} />
            <p className="text-lg" style={{ color: theme?.text_color ? theme.text_color + 'aa' : '#6b7280' }}>
              {selectedCategory !== 'all'
                ? 'Nenhum produto nesta categoria'
                : 'Nenhum produto disponível no momento'
              }
            </p>
            {selectedCategory !== 'all' && (
              <button
                onClick={() => setSelectedCategory('all')}
                className="mt-4 hover:underline text-sm font-medium"
                style={{ color: theme?.primary_color || '#4f46e5' }}
              >
                Ver todos os produtos
              </button>
            )}
          </div>
        ) : (
          <div className={`grid grid-cols-2 ${
            (theme?.products_per_row || 3) === 2 ? 'lg:grid-cols-2' :
            (theme?.products_per_row || 3) === 4 ? 'md:grid-cols-3 lg:grid-cols-4' :
            'md:grid-cols-3'
          } gap-4 md:gap-6`}>
            {products.map((product) => {
              const inCart = cart.find(item => item.product_id === product.product_id);
              const productImages = product.images?.length > 0 ? product.images : (product.image_url ? [product.image_url] : []);

              return (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  images={productImages}
                  inCart={inCart}
                  paymentsEnabled={paymentsEnabled}
                  onAddToCart={() => addToCart(product)}
                  theme={theme}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* FOOTER INSTITUCIONAL */}
      <footer
        className="mt-8 pt-12 pb-8 border-t"
        style={{
          backgroundColor: theme?.card_color || '#ffffff',
          borderColor: '#e5e7eb',
          color: theme?.text_color || '#111827'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              {theme?.profile_image ? (
                <img src={theme.profile_image} alt="Logo" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${theme?.primary_color || '#4f46e5'}, ${theme?.secondary_color || '#7c3aed'})` }}
                >
                  <Store className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="font-bold">{theme?.store_name || store.name}</span>
            </div>
            <p className="text-xs opacity-70 leading-relaxed max-w-xs">
              Loja oficial. Todos os produtos são de responsabilidade do vendedor.
              ID da Loja: {storeId}
            </p>
          </div>

          <div>
            <div className="text-xs font-bold tracking-widest uppercase mb-3 opacity-60">Formas de pagamento</div>
            <div className="flex flex-wrap gap-2">
              {['Pix', 'Crédito', 'Boleto', '12x sem juros'].map((m) => (
                <span key={m} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: '#e5e7eb' }}>{m}</span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-bold tracking-widest uppercase mb-3 opacity-60">Segurança</div>
            <div className="flex items-center gap-2 text-xs opacity-80">
              <ShieldCheck className="w-4 h-4" style={{ color: theme?.primary_color || '#4f46e5' }} />
              Ambiente protegido • Dados criptografados
            </div>
            <div className="flex items-center gap-2 text-xs opacity-80 mt-2">
              <Truck className="w-4 h-4" style={{ color: theme?.primary_color || '#4f46e5' }} />
              Envio para todo o Brasil
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t text-xs opacity-60 text-center" style={{ borderColor: '#e5e7eb' }}>
          Vitrine construída com <span className="font-semibold">Needi</span> — a plataforma de vitrines digitais.
        </div>
      </footer>

      {/* Modal do Carrinho */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Seu Carrinho
            </DialogTitle>
          </DialogHeader>

          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Carrinho vazio</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 mt-4">
                {cart.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                  >
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
                      <p className="text-indigo-600 font-bold">
                        R$ {item.unit_price.toFixed(2)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, -1)}
                        className="w-8 h-8 p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      
                      <span className="w-8 text-center font-medium">
                        {item.quantity}
                      </span>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, 1)}
                        className="w-8 h-8 p-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t pt-4 mt-4">
                {/* Cupom */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Cupom de Desconto
                  </label>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="font-mono font-medium text-emerald-700">
                          {appliedCoupon.code}
                        </span>
                        <span className="text-emerald-600 text-sm">
                          -R$ {appliedCoupon.discount_amount.toFixed(2)}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={removeCoupon}
                        className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Digite o código"
                        className="flex-1 font-mono uppercase"
                        data-testid="coupon-input"
                      />
                      <Button
                        onClick={handleValidateCoupon}
                        disabled={validatingCoupon}
                        variant="outline"
                        data-testid="apply-coupon-btn"
                      >
                        {validatingCoupon ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Ticket className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Subtotal e desconto */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span>R$ {getCartTotal().toFixed(2)}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Desconto</span>
                      <span>-R$ {appliedCoupon.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-lg font-medium text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-indigo-600">
                      R$ {getFinalTotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={processingCheckout}
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                  data-testid="checkout-btn"
                >
                  {processingCheckout ? (
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-5 h-5 mr-2" />
                  )}
                  {isAuthenticated ? 'Continuar para Checkout' : 'Fazer Login para Comprar'}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Pagamento seguro via Mercado Pago
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Form Dialog */}
      <Dialog open={showCheckoutForm} onOpenChange={setShowCheckoutForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="checkout-form-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Dados para Entrega
            </DialogTitle>
            <p id="checkout-form-desc" className="text-sm text-gray-500">Preencha seus dados para finalizar a compra</p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Dados Pessoais */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <User className="w-4 h-4" /> Dados Pessoais
              </h3>

              <div>
                <Label className="text-xs">Nome completo *</Label>
                <Input
                  value={checkoutInfo.full_name}
                  onChange={e => setCheckoutInfo(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Seu nome completo"
                  className="mt-1"
                  data-testid="checkout-full-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">CPF</Label>
                  <Input
                    value={formatCPFInput(checkoutInfo.cpf || '')}
                    onChange={e => setCheckoutInfo(p => ({ ...p, cpf: e.target.value.replace(/\D/g, '') }))}
                    placeholder="000.000.000-00"
                    className="mt-1"
                    data-testid="checkout-cpf"
                  />
                </div>
                <div>
                  <Label className="text-xs">Telefone</Label>
                  <Input
                    value={formatPhoneInput(checkoutInfo.phone || '')}
                    onChange={e => setCheckoutInfo(p => ({ ...p, phone: e.target.value.replace(/\D/g, '') }))}
                    placeholder="(00) 00000-0000"
                    className="mt-1"
                    data-testid="checkout-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  value={checkoutInfo.email}
                  onChange={e => setCheckoutInfo(p => ({ ...p, email: e.target.value }))}
                  placeholder="seu@email.com"
                  className="mt-1"
                  data-testid="checkout-email"
                />
              </div>
            </div>

            {/* Endereço */}
            <div className="space-y-3 pt-3 border-t">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> Endereço de Entrega
              </h3>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">CEP</Label>
                  <Input
                    value={checkoutInfo.address.cep || ''}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                      setCheckoutInfo(p => ({ ...p, address: { ...p.address, cep: val } }));
                      if (val.length === 8) handleCheckoutCepLookup(val);
                    }}
                    placeholder="00000000"
                    className="mt-1"
                    data-testid="checkout-cep"
                  />
                  {cepLoading && <p className="text-xs text-blue-500 mt-0.5">Buscando...</p>}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Rua</Label>
                  <Input
                    value={checkoutInfo.address.street || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, street: e.target.value } }))}
                    placeholder="Rua das Flores"
                    className="mt-1"
                    data-testid="checkout-street"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Número</Label>
                  <Input
                    value={checkoutInfo.address.number || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, number: e.target.value } }))}
                    placeholder="100"
                    className="mt-1"
                    data-testid="checkout-number"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Complemento</Label>
                  <Input
                    value={checkoutInfo.address.complement || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, complement: e.target.value } }))}
                    placeholder="Apto, bloco..."
                    className="mt-1"
                    data-testid="checkout-complement"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Bairro</Label>
                  <Input
                    value={checkoutInfo.address.neighborhood || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, neighborhood: e.target.value } }))}
                    placeholder="Centro"
                    className="mt-1"
                    data-testid="checkout-neighborhood"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cidade</Label>
                  <Input
                    value={checkoutInfo.address.city || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, city: e.target.value } }))}
                    placeholder="São Paulo"
                    className="mt-1"
                    data-testid="checkout-city"
                  />
                </div>
                <div>
                  <Label className="text-xs">UF</Label>
                  <Input
                    value={checkoutInfo.address.state || ''}
                    onChange={e => setCheckoutInfo(p => ({ ...p, address: { ...p.address, state: e.target.value.toUpperCase().slice(0,2) } }))}
                    placeholder="SP"
                    className="mt-1"
                    data-testid="checkout-state"
                  />
                </div>
              </div>
            </div>

            {/* Observações */}
            <div className="pt-3 border-t">
              <Label className="text-xs">Observações do pedido (opcional)</Label>
              <textarea
                value={checkoutInfo.order_notes}
                onChange={e => setCheckoutInfo(p => ({ ...p, order_notes: e.target.value }))}
                placeholder="Instruções especiais de entrega, cor preferida, etc."
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[60px] focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="checkout-notes"
              />
            </div>

            {/* Resumo do Pedido */}
            <div className="bg-gray-50 rounded-lg p-3 border">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Itens: {cart.reduce((s, i) => s + i.quantity, 0)}</span>
                <span className="font-bold text-gray-900">R$ {getFinalTotal().toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500">Pagamento seguro via Mercado Pago</p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCheckoutForm(false)} className="flex-1" data-testid="checkout-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
              </Button>
              <Button
                onClick={processCheckout}
                disabled={savingCheckoutInfo}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                data-testid="checkout-confirm-btn"
              >
                {savingCheckoutInfo ? (
                  <Loader className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-1" />
                )}
                Finalizar Compra
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
